<template>
  <div class="reviews-analyst-comparison-table">
    <subtitle-component>
      {{ $t("message.reviews.analyst.subtitleComparison") }}
      <div class="csv-export">
        <v-menu
          :close-on-content-click="true"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <ui-button
              v-on="on"
              color="primary"
              :loading="loading.exportBtn"
              append-icon="icon-arrow-down"
            >
              {{ $t("message.common.exportTo") }}
            </ui-button>
          </template>
          <v-list class="csv-export__list">
            <v-list-item
              class="csv-export__list-item"
              @click="exportData(EXPORT_FILE_EXTENSION.CSV)"
            >
              {{ $t("message.common.csv") }}
            </v-list-item>
            <v-list-item
              class="csv-export__list-item"
              @click="exportData(EXPORT_FILE_EXTENSION.XLSX)"
            >
              {{ $t("message.common.xlsx") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </subtitle-component>
    <v-card class="comparison-table-container">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="periods">
          <div class="first-period">
            {{
              $t("message.reviews.analyst.selectedPeriod", [firstPeriodText])
            }}
          </div>
          <div class="second-period" v-if="!isSamePeriods">
            {{
              $t("message.reviews.analyst.comparedPeriod", [secondPeriodText])
            }}
          </div>
        </div>
        <v-text-field
          v-model="facilitiesSearchInputValue"
          :placeholder="
            $t(
              'message.businessAddresses.groups.facilities.filterInputPlaceholder'
            )
          "
          prepend-inner-icon="icon-search"
          clearable
          outlined
          rounded
          class="mx-4"
          :hide-details="true"
          @input="onFacilitySearch"
        ></v-text-field>
        <div class="flex-grow-1">
          <Pagination
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="(statsTable && statsTable.count) || 0"
            @change="onPagination"
          ></Pagination>
        </div>
      </v-card-title>

      <v-data-table
        v-if="statsTable && statsTable.count"
        :headers="headers"
        :items="stats"
        :page.sync="page"
        :items-per-page="perPage"
        hide-default-footer
        disable-sort
        class="comparison-table"
      >
        <template
          v-for="header in headers"
          v-slot:[getSlotNameByHeader(header)]
        >
          <ReviewsAnalystComparisonTableHeader
            :header="header"
            :key="header.value"
            :sort-enabled="sortEnabled"
            @sort="sort"
          />
        </template>

        <template #item.gmbAddress="{item: {gmbAddress}}">
          <div>
            <div class="gmb-name">{{ gmbAddress[0] }}</div>
            <div class="gmb-address">{{ gmbAddress[1] }}</div>
            <div v-if="gmbAddress[2]" class="gmb-store-code">
              | {{ $t("message.common.storeCode") }}: {{ gmbAddress[2] }}
            </div>
          </div>
        </template>

        <template
          v-for="{
            field,
            slotName,
            inversedArrows
          } in getComparisionCellSlotsData()"
          v-slot:[slotName]="{ item }"
        >
          <ReviewsAnalystComparisonTableCell
            :key="slotName"
            :value="item[field][0]"
            :old-value="item[field][1]"
            :inversed-arrows="inversedArrows"
            :show-old-value="!isSamePeriods"
          >
            {{ item[field] }}
          </ReviewsAnalystComparisonTableCell>
        </template>
      </v-data-table>
      <NoData :fill="true" class="mt-0 pt-0 mb-16 pb-16" v-else></NoData>
      <v-card-actions class="d-flex">
        <div class="flex-grow-1">
          <Pagination
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="(statsTable && statsTable.count) || 0"
            :show-options="false"
            @change="onPagination"
          ></Pagination>
        </div>
      </v-card-actions>
      <SectionLoader :status="loading.view" :opacity="0.75" />
    </v-card>
  </div>
</template>

<script>
import SubtitleComponent from "@/components/ui/Subtitle/Subtitle.component.vue";
import SectionLoader from "@/components/Loader/SectionLoader";
import Pagination from "@/components/List/Pagination";
import ReviewsAnalystComparisonTableCell from "@/components/Reviews/Analyst/ReviewsAnalystComparisonTableCell";
import NoData from "@/components/ui/NoData";
import ReviewsAnalystComparisonTableHeader from "@/components/Reviews/Analyst/ReviewsAnalystComparisonTableHeader";

import moment from "moment";
import { debounce } from "@/helpers/helpers";
import fileDownload from "js-file-download";
import serviceHandler from "@/helpers/handlers/service";
import gmbReviewsService from "@/services/gmbReviews.js";
import { EXPORT_FILE_EXTENSION } from "@/enums/file-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "ReviewsAnalystComparisonTable",
  components: {
    SubtitleComponent,
    SectionLoader,
    Pagination,
    ReviewsAnalystComparisonTableCell,
    NoData,
    ReviewsAnalystComparisonTableHeader
  },

  props: {
    gmbAddressesIds: {
      type: Array,
      default: () => []
    },
    cities: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    firstPeriod: {
      type: Array,
      default: () => []
    },
    secondPeriod: {
      type: Array,
      default: () => []
    },
    onlyComments: {
      type: Boolean,
      default: false
    },
    onlyAccurate: {
      type: Boolean,
      default: false
    },
    reviewAction: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      firstPeriodText: "",
      statsTable: null,
      loading: {
        view: false,
        exportBtn: false
      },
      headers: [],
      stats: [],
      page: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100, 200],
      facilitiesSearchInputValue: "",
      EXPORT_FILE_EXTENSION,
      sortEnabled: true,
      sortKey: ""
    };
  },

  computed: {
    secondPeriodText() {
      return this.formatPeriodDates(this.secondPeriod);
    },
    isSamePeriods() {
      return this.firstPeriod === this.secondPeriod;
    }
  },

  watch: {
    statsTable() {
      this.setStats();
    }
  },

  async created() {
    this.setHeaders();
    await this.getStatsTable();
  },

  methods: {
    async getStatsTable() {
      await this.$nextTick();
      this.loading.view = true;

      // update text when data are being loaded (avoid reactivity of label)
      this.firstPeriodText = this.formatPeriodDates(this.firstPeriod);

      this.statsTable = await serviceHandler(
        gmbReviewsService.getStatsTable({
          firstPeriod: this.firstPeriod,
          secondPeriod: this.secondPeriod,
          gmbAddressIds: this.gmbAddressesIds,
          cities: this.cities,
          groups: this.groups,
          page: this.page,
          perPage: this.perPage,
          sortBy: this.sortKey,
          comment: this.onlyComments,
          is_accurate: this.onlyAccurate,
          reviewAction: this.reviewAction,
          query: this.facilitiesSearchInputValue
        })
      );
      this.loading.view = false;
    },

    async exportData(downloadFileExtension) {
      await this.$nextTick();
      this.loading.exportBtn = true;

      // GTM event data collection below
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.EXPORT_LOCATIONS_RATINGS,
        format: downloadFileExtension
      });
      // end GTM event stuff

      const fileResponse = await gmbReviewsService.getStatsTable({
        firstPeriod: this.firstPeriod,
        secondPeriod: this.secondPeriod,
        gmbAddressIds: this.gmbAddressesIds,
        cities: this.cities,
        groups: this.groups,
        page: this.page,
        perPage: this.perPage,
        sortBy: this.sortKey,
        downloadFileExtension,
        query: this.facilitiesSearchInputValue,
        comment: this.onlyComments,
        is_accurate: this.onlyAccurate,
        reviewAction: this.reviewAction
      });
      const fileName = fileResponse.headers["content-disposition"]
        .split("filename=")[1]
        .replace(/['"]+/g, "");

      const fileData = fileResponse.data;
      fileDownload(fileData, fileName);
      this.loading.exportBtn = false;
    },

    setHeaders() {
      const commonProps = {
        class: "header-cell",
        width: "120px",
        divider: true,
        sortable: true,
        sortDesc: null,
        align: "start"
      };
      this.headers = [
        {
          text: this.$t("message.reviews.analyst.gmbAddress"),
          value: "gmbAddress",
          divider: commonProps.divider,
          class: commonProps.class,
          sortable: false,
          align: "start"
        },
        {
          text: this.$t("message.reviews.analyst.averageRatingFromBegin"),
          value: "allAvgRating",
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.ratingCountFromBegin"),
          value: "allRatingCount",
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.averageRating"),
          value: "avgRating",
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.newRatingCount"),
          value: "ratingCount",
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.reviews"),
          value: "ratingDistribution5",
          rating: 5,
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.reviews"),
          value: "ratingDistribution4",
          rating: 4,
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.reviews"),
          value: "ratingDistribution3",
          rating: 3,
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.reviews"),
          value: "ratingDistribution2",
          rating: 2,
          ...commonProps
        },
        {
          text: this.$t("message.reviews.analyst.reviews"),
          value: "ratingDistribution1",
          rating: 1,
          ...commonProps
        }
      ];
    },

    setStats() {
      this.stats = this.statsTable.results.map(({ gmb_address, data }) => {
        const { all, first_period, second_period } = data;
        return {
          gmbAddress: [
            gmb_address.name,
            gmb_address.formatted_address,
            gmb_address.store_code
          ],
          allAvgRating: all.avg_rating,
          allRatingCount: all.count,
          avgRating: [first_period.avg_rating, second_period.avg_rating],
          ratingCount: [first_period.count, second_period.count],
          ratingDistribution1: [
            first_period.rating_distribution["1"],
            second_period.rating_distribution["1"]
          ],
          ratingDistribution2: [
            first_period.rating_distribution["2"],
            second_period.rating_distribution["2"]
          ],
          ratingDistribution3: [
            first_period.rating_distribution["3"],
            second_period.rating_distribution["3"]
          ],
          ratingDistribution4: [
            first_period.rating_distribution["4"],
            second_period.rating_distribution["4"]
          ],
          ratingDistribution5: [
            first_period.rating_distribution["5"],
            second_period.rating_distribution["5"]
          ]
        };
      });
    },

    getRatingHeaderSlotName(rating) {
      return `header.ratingDistribution${rating}`;
    },

    getSlotNameByHeader(header) {
      return `header.${header.value}`;
    },

    getComparisionCellSlotsData() {
      const comparisionFields = [
        { field: "avgRating" },
        { field: "ratingCount" },
        { field: "ratingDistribution5" },
        { field: "ratingDistribution4" },
        { field: "ratingDistribution3", inversedArrows: true },
        { field: "ratingDistribution2", inversedArrows: true },
        { field: "ratingDistribution1", inversedArrows: true }
      ];

      return comparisionFields.map(item => ({
        ...item,
        slotName: `item.${item.field}`
      }));
    },

    onFacilitySearch: debounce(async function() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEWS_ANALYTICS_SEARCH
      });
      this.page = 1;
      await this.getStatsTable();
    }, 500),

    onPagination({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      this.getStatsTable();
    },

    formatPeriodDates(dates) {
      const formattedDates = dates.map(date =>
        moment(date).format("DD.MM.YYYY")
      );
      return formattedDates.length === 2 &&
        formattedDates[0] === formattedDates[1]
        ? formattedDates[0]
        : formattedDates.join("-");
    },
    sort({ header }) {
      this.headers.forEach(item => {
        if (item.value !== header.value) {
          item.sortDesc = null;
        } else {
          this.updateSort(item);
        }
      });
      this.sortKey = this.getSortKey(header);
      this.getStatsTable();
    },
    updateSort(item) {
      if (item.sortDesc == null) {
        item.sortDesc = false;
      } else if (item.sortDesc == false) {
        item.sortDesc = true;
      } else {
        item.sortDesc = null;
      }
    },
    getSortKey(header) {
      if (header.sortDesc !== null) {
        return `${header.sortDesc ? "-" : ""}${header.value}`;
      }
      return "";
    }
  }
};
</script>

<style lang="scss">
.reviews-analyst-comparison-table {
  height: 100%;
  display: flex;
  flex-direction: column;

  .comparison-table-container {
    flex: 1;
    padding-bottom: $spacing * 1.75;

    .v-card__title,
    .v-card__actions {
      padding: $spacing * 2 $spacing * 2 $spacing * 2 $spacing * 3.75;
    }
  }

  .periods {
    padding-left: $spacing * 2;
  }

  .first-period {
    font-family: $font-family-base;
    @include font-size(18px);
    line-height: 22px;
    color: $primary;
  }

  .second-period {
    font-family: $font-family-base;
    @include font-size(14px);
    font-weight: 400;
    line-height: 17px;
    color: $grey-darker;
    margin-top: $spacing * 0.75;
  }

  .comparison-table {
    padding: $spacing * 3.75;

    &.theme--light.v-data-table {
      tbody tr:not(:last-child) td:not(.v-data-table__mobile-row),
      thead tr:last-child th {
        border-bottom: 0 !important;
        position: relative;

        &::before {
          content: "";
          position: absolute;
          bottom: 0;
          left: 18px;
          right: 18px;
          height: 1px;
          background: $table-border-color;
        }
      }

      tbody tr td:not(.v-data-table__mobile-row) {
        border-right: 1px solid $table-border-color;

        &:nth-of-type(3) {
          border-right-color: $table-border-alt-color !important;
        }
      }

      thead tr:last-child th {
        border-right: 0;

        &::after {
          content: "";
          position: absolute;
          right: 0;
          top: 10px;
          bottom: 10px;
          width: 1px;
          background: $table-border-color;
        }

        &:nth-of-type(3)::after {
          background: $table-border-alt-color;
        }
      }
    }

    &.v-data-table td {
      @include font-size(18px);
      font-weight: 400;
      color: $grey-darker;
      user-select: none;
    }

    &.v-data-table td:first-of-type {
      user-select: text;
    }

    .header-cell {
      @include font-size(12px);
      font-family: $font-family-base;
      font-weight: 400;
      color: $grey-darker;
      line-height: 15px;
      height: 50px;
      padding: $spacing * 2;
      text-align: center;

      .v-rating--dense .v-icon {
        padding: 0;
      }

      &:first-of-type {
        padding-left: $spacing * 2;
      }
    }

    .gmb-name {
      @include font-size(14px);
      color: $dark;
      line-height: 18px;
      font-weight: 500;
    }

    .gmb-address {
      @include font-size(12px);
      color: $dark;
      line-height: 15px;
      font-weight: 400;
      opacity: 0.8;
    }

    .gmb-store-code {
      @include font-size(10px);
      color: $dark;
      line-height: 15px;
      font-weight: 600;
      opacity: 0.8;
    }
  }
}

.csv-export {
  margin-left: auto;
  display: flex;
  align-items: center;

  p {
    @include font-size(14px);
    font-weight: 500;
    color: $text;
    margin-bottom: 0;
    margin-right: $spacing * 2;
  }
}
</style>
